import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatAiCredits } from "@/lib/formatAiCredits";
import {
  applyPostAuthRedirect,
  saveAdminReturnSession,
  setAuthSessionCookies,
} from "@/lib/authCookies";
import ComplimentaryBillingForm from "../components/ComplimentaryBillingForm";
import AdminDeleteUserModal from "../components/AdminDeleteUserModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [assuming, setAssuming] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const token = Cookies.get("token");
        const res = await axios.get(`${BACKEND_URL}/api/admin/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to load user");
      }
    };
    load();
  }, [id]);

  const handleAssume = async () => {
    setAssuming(true);
    try {
      const adminToken = Cookies.get("token");
      const adminEmail = Cookies.get("email");
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/users/${id}/assume`,
        {},
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      const data = res.data;
      saveAdminReturnSession({
        token: adminToken,
        adminEmail,
        targetEmail: data.target.email,
        targetName: data.target.name,
      });
      setAuthSessionCookies({
        token: data.token,
        email: data.target.email,
        accountType: data.account_type,
        botType: data.target.bot_type,
      });
      toast.success(`Opened session as ${data.target.email}`);
      applyPostAuthRedirect(navigate, data.default_route || "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assume session");
    } finally {
      setAssuming(false);
    }
  };

  if (error) {
    return (
      <div>
        <Link to="/admin/users" className="text-[#00D1FF] text-sm inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to users
        </Link>
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!user) {
    return <p className="text-[#9CA3AF]">Loading user…</p>;
  }

  const bal = user.credit_balance;

  return (
    <div data-testid="admin-user-detail">
      <Link to="/admin/users" className="text-[#00D1FF] text-sm inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to users
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold">{user.email}</h1>
          <p className="text-[#9CA3AF] text-sm mt-1 capitalize">
            {user.account_type}
            {user.bot_type ? ` · ${user.bot_type}` : ""}
          </p>
        </div>
        {user.account_type !== "admin" && (
          <button
            type="button"
            onClick={handleAssume}
            disabled={assuming}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#00D1FF]/30 text-[#00D1FF] text-sm hover:bg-[#00D1FF]/10 disabled:opacity-50"
          >
            <ExternalLink className="w-4 h-4" />
            {assuming ? "Opening…" : "Open as user"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-3">
          <h2 className="font-heading font-semibold">Account</h2>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between gap-4">
              <dt className="text-[#9CA3AF]">AI tier</dt>
              <dd>{user.ai_tier ?? "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-[#9CA3AF]">Billing plan</dt>
              <dd className="capitalize">{user.billing_plan}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-[#9CA3AF]">Partner</dt>
              <dd>{user.partner_email || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-[#9CA3AF]">Status</dt>
              <dd>{user.deployment_status || "—"}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-[#9CA3AF]">Created</dt>
              <dd>{user.created_at ? new Date(user.created_at).toLocaleString() : "—"}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-3">
          <h2 className="font-heading font-semibold">Credits</h2>
          {bal ? (
            <dl className="text-sm space-y-2">
              <div className="flex justify-between gap-4">
                <dt className="text-[#9CA3AF]">Used / included</dt>
                <dd>
                  {formatAiCredits(bal.credits_used)} / {formatAiCredits(bal.credits_included)}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[#9CA3AF]">Period</dt>
                <dd className="text-right text-xs text-[#9CA3AF]">
                  {bal.period_start ? new Date(bal.period_start).toLocaleDateString() : "—"}
                  {" – "}
                  {bal.period_end ? new Date(bal.period_end).toLocaleDateString() : "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-[#9CA3AF]">Source</dt>
                <dd>{bal.billing_source || "—"}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm text-[#9CA3AF]">No credit balance on record</p>
          )}

          {user.subscription && (
            <>
              <h3 className="font-heading font-semibold pt-2">Subscription</h3>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between gap-4">
                  <dt className="text-[#9CA3AF]">Status</dt>
                  <dd>{user.subscription.status}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-[#9CA3AF]">Plan</dt>
                  <dd>{user.subscription.internal_plan_id || "—"}</dd>
                </div>
              </dl>
            </>
          )}
        </div>

        {user.account_type !== "admin" && (
          <div className="lg:col-span-2 rounded-xl border border-white/10 bg-white/[0.03] p-5">
            <h2 className="font-heading font-semibold mb-4">Billing management</h2>
            <ComplimentaryBillingForm user={user} onUpdated={setUser} />
          </div>
        )}

        {user.account_type !== "admin" && (
          <div className="lg:col-span-2 rounded-xl border border-red-500/20 bg-red-500/[0.03] p-5">
            <h2 className="font-heading font-semibold text-red-400 mb-2">Danger zone</h2>
            <p className="text-sm text-[#9CA3AF] mb-4">
              Permanently delete this user and all associated application data.
            </p>
            <button
              type="button"
              onClick={() => setDeleteModalOpen(true)}
              data-testid="admin-delete-user-button"
              className="px-4 py-2 rounded-lg bg-red-600/90 text-sm font-medium text-white hover:bg-red-500"
            >
              Delete user and all data
            </button>
          </div>
        )}
      </div>

      <AdminDeleteUserModal
        user={user}
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDeleted={() => navigate("/admin/users")}
      />
    </div>
  );
};

export default AdminUserDetail;
